/**
 * Handles search box changes and resets to first page.
 * @param {Object} event - Kendo search box change event.
 * @param {Function} setSearchValue - State setter for search value.
 * @param {Function} setDataState - State setter for dataState.
 */
const handleSearchChange = (event, setSearchValue, setDataState) => {
    setSearchValue(event.value);
    setDataState(ds => ({ ...ds, skip: 0 }));
}

/**
 * Handles Kendo Grid data state changes (paging, etc).
 * @param {Object} event - Kendo Grid dataStateChange event.
 * @param {Function} setDataState - State setter for dataState.
 */
const handleRenderedDataChange = (event, setDataState) => {
    setDataState(event.dataState);
}

/**
 * Handles filter changes and resets to first page.
 * @param {Object} event - Kendo Grid filterChange event.
 * @param {Function} setFilter - State setter for filter.
 * @param {Function} setDataState - State setter for dataState.
 */
const handleFilterChange = (event, setFilter, setDataState) => {
    setFilter(event.filter);
    setDataState(ds => ({ ...ds, skip: 0 }));
}

/**
 * Handles group changes and resets to first page.
 * @param {Object} params - Parameters for handling group changes.
 * @param {Object} params.event - Kendo Grid groupChange event.
 * @param {Function} params.setGroup - State setter for group.
 * @param {Function} params.setDataState - State setter for dataState.
 * @param {number} params.pageSize - Current page size.
 * @param {number} params.dataLength - The processed grid data array length.
 */
const handleGroupChange = ({event, setGroup, setDataState, pageSize, dataLength}) => {
    if (!event || !Array.isArray(event.group)) {
        setGroup([]);
        setDataState(ds => ({ ...ds, skip: 0, take: pageSize }));
        return;
    }
    const uniqueGroups = event.group.filter(
      (item, index, self) =>
        item && item.field && index === self.findIndex((g) => g.field === item.field)
    );
    setGroup(uniqueGroups);
    setDataState(ds => ({ ...ds, skip: 0, take: uniqueGroups.length ? dataLength : pageSize }));
}

/**
 * Handles sort changes.
 * @param {Object} event - Kendo Grid sortChange event.
 * @param {Function} setSort - State setter for sort.
 */
const handleSortChange = (event, setSort) => {
    setSort(event.sort);
}

/**
 * Handles selection changes.
 * @param {Object} event - Kendo Grid selectionChange event.
 * @param {Function} setSelect - State setter for selection.
 */
const handleSelectionChange = (event, setSelect) => {
    setSelect(event.select);
}

/**
 * Handles row expand/collapse logic for Kendo Grid detail rows.
 * @param {Object} params - Parameters for handling expand/collapse.
 * @param {Object|null} params.event - Kendo Grid detailExpand event or null for expand/collapse all.
 * @param {Array} params.resultData - The processed grid data array.
 * @param {string} params.dataItemKey - Unique key for data items.
 * @param {Object} params.detailExpanded - Current expanded state object.
 * @param {Function} params.setDetailExpanded - State setter for expanded state.
 */
const handleExpandChange = ({ event, resultData, dataItemKey, detailExpanded, setDetailExpanded }) => {
    if (!Array.isArray(resultData) || !dataItemKey || typeof setDetailExpanded !== 'function' || !detailExpanded) return;
    const pageRowIds = resultData.map(item => item && item[dataItemKey]).filter(Boolean);
    if (event && event.detailExpand) {
        const changedRowId = Object.keys(event.detailExpand).find(
            id => event.detailExpand[id] !== detailExpanded[id]
        );
        if (changedRowId) {
            setDetailExpanded(prev => ({
                ...prev,
                [changedRowId]: event.detailExpand[changedRowId]
            }));
        }
    } else {
        const anyPageRowsExpanded = pageRowIds.some(id => detailExpanded[id]);
        setDetailExpanded(prev => {
            const updated = { ...prev };
            pageRowIds.forEach(id => {
                updated[id] = !anyPageRowsExpanded;
            });
            return updated;
        });
    }
}

/**
 * Handles Excel export for the table.
 * @param {Function} onExportExcel - Optional callback for export logic.
 * @param {Object} excelExportRef - Ref to the ExcelExport component.
 */
const handleExport = (onExportExcel, excelExportRef) => {
    if (typeof onExportExcel === 'function') onExportExcel();
    if (excelExportRef && excelExportRef.current && typeof excelExportRef.current.save === 'function') {
        excelExportRef.current.save();
    }
}

export {
    handleSearchChange,
    handleRenderedDataChange,
    handleFilterChange,
    handleGroupChange,
    handleSortChange,
    handleSelectionChange,
    handleExpandChange,
    handleExport
};